export type StatusTone = 'success' | 'warning' | 'info' | 'danger' | 'neutral';

export interface StatusDefinition {
  label: string;
  tone: StatusTone;
}

type StatusDomain = Record<string, StatusDefinition>;

const status = (label: string, tone: StatusTone): StatusDefinition => ({ label, tone });

export const STATUS_TONES = {
  event: {
    pending: status('Pendente', 'warning'),
    ready: status('Pronto para Operação', 'success'),
    disabled: status('Desabilitado', 'danger'),
    done: status('Realizado', 'info')
  },
  closure: {
    consolidated: status('Consolidado', 'success'),
    consolidating: status('Recalculando', 'info'),
    consolidated_with_problems: status('Divergente', 'danger'),
    approved: status('Aprovado', 'success'),
    awaiting_analysis: status('Aguardando Análise', 'warning'),
    blocked: status('Bloqueado', 'neutral')
  },
  creditAdjustOperation: {
    approved: status('Aprovada', 'success'),
    rejected: status('Reprovada', 'danger'),
    waiting: status('Aguardando', 'warning')
  },
  contractRequest: {
    registered: status('Cadastrado', 'neutral'),
    approved: status('Aprovado', 'success'),
    awaiting: status('Aguardando', 'warning'),
    blocked: status('Bloqueado', 'neutral'),
    canceled: status('Cancelado', 'danger'),
    signed: status('Assinado', 'info')
  },
  refund: {
    approved: status('Aprovado', 'success'),
    refused: status('Recusado', 'danger'),
    registered: status('Registrado', 'neutral'),
    waiting: status('Em análise', 'warning')
  },
  advance: {
    requested: status('Solicitado', 'info'),
    refused: status('Recusado', 'danger'),
    processing: status('Processando', 'warning'),
    awaiting_payment: status('Aguardando Pagamento', 'warning'),
    processed: status('Realizado', 'success')
  },
  invoice: {
    authorized: status('Autorizada', 'success'),
    cancelled: status('Cancelada', 'warning'),
    processing_cancelation: status('Processando cancelamento', 'info'),
    cancelation_error: status('Autorizada', 'success')
  },
  nfe: {
    authorized: status('Autorizada', 'success'),
    cancelled: status('Cancelada', 'warning'),
    pending: status('Pendente', 'warning'),
    unauthorized: status('Não Autorizada', 'danger')
  },
  commercialInvoice: {
    opened: status('Aberta', 'info'),
    closed: status('Fechada', 'success')
  },
  productFiscal: {
    none: status('Não possui informações fiscais', 'neutral'),
    pending: status('Cadastro fiscal pendente', 'warning'),
    success: status('Informações fiscais válidas', 'success'),
    failure: status('Erro ao cadastrar informações fiscais', 'danger')
  },
  ticketValidation: {
    true: status('Sucesso', 'success'),
    false: status('Falha', 'danger')
  },
  eventIntegration: {
    pending: status('Pendente', 'warning'),
    running: status('Processando', 'success'),
    completed: status('Finalizada', 'info')
  },
  eventIntegrationAttendee: {
    automatic: status('Automático', 'success'),
    synchronizing: status('Sincronizando', 'warning')
  },
  user: {
    enable: status('Habilitado', 'success'),
    disable: status('Desabilitado', 'danger')
  },
  device: {
    enabled: status('Habilitado', 'success'),
    disabled: status('Desabilitado', 'danger')
  },
  bank: {
    enabled: status('Ativo', 'success'),
    disabled: status('Inativo', 'neutral')
  },
  table: {
    active: status('Ativa', 'success'),
    blocked: status('Bloqueada', 'danger'),
    deprecated: status('Descontinuada/Fora de uso', 'neutral')
  },
  tableAvailability: {
    clear: status('Livre', 'success'),
    busy: status('Ocupada', 'warning')
  },
  fileUpload: {
    idle: status('Selecionado', 'neutral'),
    queued: status('Na fila', 'neutral'),
    uploading: status('Enviando', 'info'),
    compressing: status('Comprimindo', 'info'),
    processing: status('Processando', 'info'),
    completed: status('Concluído', 'success'),
    failed: status('Falhou', 'danger')
  },
  menuImageAnalysis: {
    queued: status('Na fila', 'neutral'),
    compressing: status('Comprimindo', 'info'),
    processing: status('Processando', 'info'),
    completed: status('Concluida', 'success'),
    failed: status('Falhou', 'danger')
  },
  cardActivation: {
    active: status('Ativado', 'success'),
    inactive: status('Desativado', 'danger'),
    blocked: status('Bloqueado', 'warning')
  },
  debit: {
    true: status('Debitada', 'success')
  },
  posShift: {
    opened: status('Aberto', 'success'),
    closed: status('Fechado', 'danger')
  },
  payment: {
    authorized: status('Autorizado', 'success'),
    cancelled: status('Cancelado', 'danger')
  },
  acquirerTransaction: {
    scheduled: status('Agendada', 'info'),
    paid: status('Paga', 'success'),
    available: status('Disponível', 'success'),
    antecipation: status('Antecipada', 'info'),
    requested: status('Solicitada', 'warning')
  }
} satisfies Record<string, StatusDomain>;

export type StatusDomainName = keyof typeof STATUS_TONES;

export function statusFor(domain: StatusDomainName, value: string | boolean | null | undefined): StatusDefinition {
  const key = String(value ?? '').toLowerCase();
  const definitions: StatusDomain = STATUS_TONES[domain];

  return definitions[key] || status(String(value ?? ''), 'neutral');
}
